import { EmptyView, emptyViewVariants } from '@components'

import { ModuleLayout } from '@billy/design-system'
import { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'

import { Scope } from '../../../../enums/scope'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useIsQuotesListEmpty } from '../QuotesList/hooks/useIsQuotesListEmpty'
import { useQuotesListFilters } from '../QuotesList/hooks/useQuotesListFilters'

export const QuotesListEmpty = (): ReactElement | null => {
  const { t } = useTranslation()
  const [, setFilters] = useQuotesListFilters()
  const { isLoading, isQuotesListEmpty } = useIsQuotesListEmpty()
  const navigate = useNavigate()

  const quoteDocumentRouteBase = getInvoiceDocumentRouteBase(InvoiceDocumentType.Quote)

  const { url: quoteUrl } = useOrganizationViewUrl(reactRoute.organization.quotes.getMainRoute())
  const { url: quoteNewUrl } = useOrganizationViewUrl(
    reactRoute.organization.invoices.getNewDocumentRoute(quoteDocumentRouteBase),
  )

  // Clear filters in case they left after redirecting from `QuotesList`
  useEffect(() => {
    setFilters({
      page: undefined,
      pageSize: undefined,
      period: undefined,
      searchQuery: undefined,
      sortDirection: undefined,
      sortProperty: undefined,
      state: undefined,
    })
  }, [setFilters])

  const { animation, description, title, buttonText } = emptyViewVariants.quotes

  const handleEmptyViewClick = useCallback(() => {
    if (quoteNewUrl) {
      navigate(quoteNewUrl)
    }
  }, [navigate, quoteNewUrl])

  if (!quoteUrl || isLoading) {
    return null
  }

  if (!isQuotesListEmpty) {
    return <Navigate to={quoteUrl} replace />
  }

  return (
    <ModuleLayout title={t('quotes')}>
      <EmptyView
        animationName={animation}
        onClick={handleEmptyViewClick}
        scopes={Scope.OfferWrite}
        showCTA={!!buttonText?.length}
        tButton={buttonText}
        tDescription={description}
        trackingContext={TrackingContext.CreateQuote}
        tTitle={title}
      />
    </ModuleLayout>
  )
}
